import { useState, FunctionComponent } from 'react';
import { Button } from '../../tailwindcomponents';
import { Typography, Input, Snackbar, Dialog } from './../../tailwindcomponents';
import CertificatesApi from '../../api/certificates'
import { useSelector } from 'react-redux';
import { RootState } from '../../reducer';
import { CertificateRights, RatingsDifference } from '../../types/certificates';
import { settings } from '../../config';

type AlertColor = 'success' | 'info' | 'warning' | 'error';

type ProcessApprovalProps = {
    approvalSendBy: string,
    changes: string[],
    closeCertificate: Function,
    certificateId: number,
    approverId: number,
    employeeId?: number,
    certificates: CertificateRights[],
    ratingsDifference: RatingsDifference
}

const ProcessApproval: FunctionComponent<ProcessApprovalProps> = ({ employeeId, closeCertificate, certificateId, approverId, changes, approvalSendBy, ratingsDifference }) => {
    const [rejectDialog, setRejectDialog] = useState(false);
    const [approveDialog, setApproveDialog] = useState(false);
    const [rejectReason, setRejectReason] = useState('');
    const [showValidation, setShowValidation] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState<string>("");
    const [snackbarSeverity, setSnackbarSeverity] = useState<AlertColor>("success");
    const rejectValidation = 'You must type a reason to continue';
    const store = useSelector((state: RootState) => state);

    const onReasonEdit = (value: string) => {
        setRejectReason(value);
    }
    const rejectCertificate = () => {
        if (rejectReason === '') {
            setShowValidation(true);
        }
        else {
            CertificatesApi.QualityAssuranceReject(certificateId, approverId, rejectReason).then(res => {
                if (res.status === true) {
                    setRejectDialog(false);
                    closeCertificate();
                    sendEmailApprovalMessage(false);
                }
            });
        }
    }
    const approveCertificate = () => {
        CertificatesApi.QualityAssuranceAccept(certificateId, approverId).then(res => {
            if (res.status === true) {
                sendEmailApprovalMessage(true);
                setSnackbarMessage(res.message || "Certificate approved successfully!");
                setSnackbarSeverity("success");
            }
            else{
                setSnackbarMessage(res.message || "An error occurred while approving the certificate.");
                setSnackbarSeverity("error");
            }
            setApproveDialog(false);
        });
    }
    const handleSnackbarClose = () => {
        setSnackbarMessage("");
        closeCertificate();
    }
    const sendEmailApprovalMessage = (approved: boolean) => {
        const employeeEmail = store.employees.all_employees.find(user => user.user_id === employeeId)?.userName || "";
        let subject = approved ? "Certificate Approval" : "Certificate Rejection";
        
        if (settings.HOST !== "https://api.icelandair.is/api/maintener-backend") {
            subject = "TEST - " + subject;
        }
        
        const ratingChanges = changes.map(change => `<li>${!approved ? change.replace("NEW", "REJECTED") : change}</li>`).join('');
        const body = `
                    <!DOCTYPE html>
                    <html>
                    <head>
                        <meta charset="UTF-8">
                        <title>Certificates</title>
                        <style>
                            body {
                                font-family: Arial, sans-serif;
                            }
                        </style>
                    </head>
                    <body>
                        <p>Dear employee</p>
                        <p>You have following changes on your certificate:</p>
                        <ul>
                            ${ratingChanges}
                        </ul>
                        <p>For more information visit <a href="https://maintener.icelandair.is/#/certificates">Maintener</a></p>
                        <p>If you have any questions or need further information, please do not hesitate to contact your supervisor.</p>
                        <p>Best regards,</p>
                        <p>Maintener</p>
                        <br/>
                        <hr/>
                        <br/>
                        <p>Kæri starfsmaður</p>
                        <p>Þú ert með eftirfarandi breytingar á skírteininu þínu:</p>
                        <ul>
                            ${ratingChanges}
                        </ul>
                        <p>Frekari upplýsingar má finna á <a href="https://maintener.icelandair.is/#/certificates">Maintener</a></p>
                        <p>Ef þú hefur einhverjar spurningar eða þarft frekari upplýsingar skaltu ekki hika við að hafa samband við yfirmann þinn.</p>
                        <p>Bestu kveðjur,</p>
                        <p>Maintener</p>
                        <br/>
                    </body>
                    </html>`;
        if (employeeEmail !== undefined) {
            let shortenedStringWithName = approvalSendBy.split(' - ')[0];
            const approvalSentByEmail = store.employees.all_employees.find(user => user.employee_name === shortenedStringWithName)?.userName || "";
            CertificatesApi.SendEmailApprovalMessage(employeeEmail, body, subject, approvalSentByEmail).then(res => { });
        }
    }
    return (
        <div className="mt-4 flex flex-wrap w-full">
            <div className="w-1/2">
                <Button variant="contained" color="primary" onClick={() => setApproveDialog(true)}>Approve and publish certificate</Button>
            </div>
            <div className="w-1/2">
                <Button variant="contained" color="secondary" onClick={() => setRejectDialog(true)}>Reject certificate</Button>
            </div>
            {/*Reject Dialog - Need to fix center placement in Dialog */}
            <Dialog
                visible={rejectDialog}
                max_width={"md"} title={"Are you sure you want to reject?"}
                context={
                    <div>
                        <div className="flex justify-center w-full m-2 p-2">
                            <Input
                                label_text={"Type a reason for rejection"} value={rejectReason} help_text=""
                                onTextChange={(value: string) => onReasonEdit(value)} type="text" width={50} multiline={false} />
                        </div>
                        <div className="flex justify-center m-2 p-2">
                            <div className="p-2" >
                                <Button variant="contained" color="primary" onClick={() => rejectCertificate()}>Reject certificate</Button>
                            </div>
                            <div className="p-2">
                                <Button variant="contained" color="secondary" onClick={() => setRejectDialog(false)}>Cancel</Button>
                            </div>
                            {showValidation &&
                                <Typography variant="body1" color="error" align="center" text={rejectValidation} />
                            }
                        </div>
                    </div>
                }
                onClose={(status: any) => setRejectDialog(false)}
                strict={false}
                show_options={false}
            />
            <Dialog
                visible={approveDialog}
                max_width={"md"} title={"Are you sure you want to approve?"}
                context={
                    <div className="flex justify-center m-2 p-2">
                        <div className="p-2" >
                            <Button variant="contained" color="primary" onClick={() => approveCertificate()}>Approve certificate</Button>
                        </div>
                        <div className="p-2" >
                            <Button variant="contained" color="secondary" onClick={() => setApproveDialog(false)}>Cancel</Button>
                        </div>
                    </div>
                }
                onClose={(status: any) => setApproveDialog(false)}
                strict={false}
                show_options={false}
            />
            <Snackbar
                onSnackBarClose={handleSnackbarClose}
                open={snackbarMessage.length > 0}
                message={snackbarMessage}
                autoHide={false}
                horizontalPosition='right'
                severity={snackbarSeverity}
            >
            </Snackbar>
        </div>
    );
};
export default ProcessApproval;
